import React, { useState, useEffect } from 'react'
import { AlertTriangle, TrendingUp, MapPin, Tag, Activity, Bell, BellOff, Filter, ChevronDown, ArrowUpRight, Clock, Shield, Zap } from 'lucide-react'
import api from '../services/api'

const severityConfig = {
  critical: { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-800', icon: 'text-red-600', dot: 'bg-red-500' },
  high: { bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-800', icon: 'text-orange-600', dot: 'bg-orange-500' },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-800', icon: 'text-amber-600', dot: 'bg-amber-500' },
  info: { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-800', icon: 'text-blue-600', dot: 'bg-blue-500' },
}

const typeConfig = {
  trend_spike: { icon: TrendingUp, label: 'Trend Spike' },
  volume_spike: { icon: Activity, label: 'Volume Spike' },
  region_spike: { icon: MapPin, label: 'Region Alert' },
  brand_spike: { icon: Tag, label: 'Brand Alert' },
  anomaly: { icon: Zap, label: 'Anomaly' },
}

const Alerts = () => {
  const [alerts, setAlerts] = useState([])
  const [summary, setSummary] = useState({ total: 0, critical: 0, high: 0, warning: 0 })
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [dismissed, setDismissed] = useState(new Set())

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const res = await api.get('/analytics/alerts')
        if (res.data?.success) {
          setAlerts(res.data.data.alerts || [])
          setSummary(res.data.data.summary || { total: 0, critical: 0, high: 0, warning: 0 })
        }
      } catch (e) { console.error(e) }
      finally { setLoading(false) }
    }
    fetchAlerts()
  }, [])

  const filteredAlerts = alerts.filter(a => {
    if (dismissed.has(a.id)) return false
    if (filter === 'all') return true
    return a.severity === filter || a.type === filter
  })

  const activeCount = alerts.filter(a => !dismissed.has(a.id)).length

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E4D40]" /></div>

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Alerts & Notifications</h1>
          <p className="text-gray-500 mt-1">Real-time trend monitoring and anomaly alerts</p>
        </div>
        <div className="flex items-center space-x-2 mt-4 sm:mt-0">
          <span className="flex items-center px-3 py-1.5 bg-[#1E4D40] text-white rounded-xl text-sm font-medium">
            <Bell className="h-4 w-4 mr-1.5" /> {activeCount} Active
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Critical', count: summary.critical, color: 'bg-red-500', textColor: 'text-red-600', bgColor: 'bg-red-50' },
          { label: 'High', count: summary.high, color: 'bg-orange-500', textColor: 'text-orange-600', bgColor: 'bg-orange-50' },
          { label: 'Warning', count: summary.warning, color: 'bg-amber-500', textColor: 'text-amber-600', bgColor: 'bg-amber-50' },
          { label: 'Total', count: summary.total, color: 'bg-[#1E4D40]', textColor: 'text-[#1E4D40]', bgColor: 'bg-gray-50' },
        ].map((s, i) => (
          <div key={i} className={`${s.bgColor} rounded-2xl p-4 border border-gray-100`}>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">{s.label}</span>
              <span className={`w-2.5 h-2.5 rounded-full ${s.color}`} />
            </div>
            <p className={`text-3xl font-bold mt-1 ${s.textColor}`}>{s.count}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          { id: 'all', label: 'All Alerts' },
          { id: 'critical', label: 'Critical' },
          { id: 'high', label: 'High' },
          { id: 'warning', label: 'Warning' },
          { id: 'trend_spike', label: 'Trend Spikes' },
          { id: 'region_spike', label: 'Region Alerts' },
          { id: 'brand_spike', label: 'Brand Alerts' },
          { id: 'anomaly', label: 'Anomalies' },
        ].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-xl text-sm font-medium transition-all ${
              filter === f.id ? 'bg-[#1E4D40] text-white' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {filteredAlerts.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 shadow-sm text-center">
          <BellOff className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900">No alerts</h3>
          <p className="text-gray-500 mt-1">All systems are operating within normal parameters.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredAlerts.map(alert => {
            const sev = severityConfig[alert.severity] || severityConfig.info
            const typeInfo = typeConfig[alert.type] || { icon: AlertTriangle, label: alert.type }
            const TypeIcon = typeInfo.icon
            return (
              <div key={alert.id} className={`${sev.bg} ${sev.border} border rounded-2xl p-5 transition-all hover:shadow-md`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${sev.bg} ${sev.icon}`}>
                      <TypeIcon className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="text-sm font-semibold text-gray-900">{alert.title}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sev.badge}`}>{alert.severity}</span>
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{typeInfo.label}</span>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">{alert.message}</p>
                      {alert.metric && (
                        <div className="flex flex-wrap gap-3 mt-3">
                          {Object.entries(alert.metric).map(([key, val]) => (
                            <span key={key} className="inline-flex items-center text-xs bg-white/80 rounded-lg px-2.5 py-1 border border-gray-200">
                              <span className="text-gray-500 mr-1">{key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}:</span>
                              <span className="font-semibold text-gray-800">{typeof val === 'number' ? val.toLocaleString() : val}{key.toLowerCase().includes('rate') || key.toLowerCase().includes('change') ? '%' : ''}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <button onClick={() => setDismissed(prev => new Set([...prev, alert.id]))}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-white/50 transition-colors shrink-0 ml-2"
                    title="Dismiss alert">
                    <BellOff className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default Alerts
